import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { MipInvestorBankAccountDataGapRes } from '@dealstack/shared/lib/generated';
import { Flex, Text, Tooltip } from '@mantine/core';

interface Props {
	bankAccountId: string;
	dataGaps: MipInvestorBankAccountDataGapRes[];
}

/**
 * A data gap is not a risk: it says a check could not run because the value it compares
 * against is missing from the participant's record. It sits beside the owner — whose
 * record is the thing incomplete — and is deliberately muted rather than red, so it
 * cannot be mistaken for the Risk column's findings.
 */
export const DataGapIndicator = ({ bankAccountId, dataGaps }: Props) => {
	if (dataGaps.length === 0) {
		return null;
	}

	return (
		<Tooltip
			multiline
			w={280}
			label={
				dataGaps.length === 1 ? (
					<Text size="xs">{dataGaps[0].message}</Text>
				) : (
					<Flex direction="column" gap="0.25rem">
						{dataGaps.map((gap) => (
							<Text key={gap.key} size="xs">
								{gap.message}
							</Text>
						))}
					</Flex>
				)
			}
			color="white.0"
			maw="240px"
			position="left"
			c="orange.6"
			style={{ border: '1px solid', borderColor: 'inherit' }}
		>
			<Flex
				align="center"
				data-testid={makeComplexDataTestId(
					TEST_ELEMENT_IDS.MIP_MANAGE_BANK_ACCOUNT_DATA_GAP_ICON,
					bankAccountId
				)}
				c="orange.6"
				w="fit-content"
			>
				<Icon variant="warning" size="lg" />
			</Flex>
		</Tooltip>
	);
};
